import type { IOption } from 'common/interfaces/IOption'
import type {
  Patient,
  PatientEncounter,
  PatientWithEncounters,
  Provider,
} from 'features/Procedure/state/procedure-api.type'
import type { IPatient, PatientDiscountApiValue } from 'features/Patients/interfaces/IPatient'
import type { EncounterData } from 'features/Profile/features/TransactionsMapping/transactionsMapping.api'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import type { PatientStatus } from 'features/Home/interfaces/IInfoSchedule'
import { UtilService } from 'common/services/utilService'
import { normalizeDateString } from 'common/services/format'

dayjs.extend(customParseFormat)

const APPOINTMENT_TIME_INPUT_FORMATS = ['HH:mm:ss', 'HH:mm', 'h:mm A', 'h:mmA', 'hh:mm A', 'hh:mmA']

export type PatientSelectOption = IOption<string> & {
  patientName: string
  staffInitials: string
  appointmentTimeLabel: string | null
  statusCode: number | null
  statusColor: string | null
  searchText: string
}

export type EncounterSelectOption = IOption<string> & {
  encounterTitle: string
  dateLabel: string
  providerInitials: string
  providerName: string
  searchText: string
  className?: string
}

export const formatPatientName = (patient: Patient): string => {
  const fullName = [patient.last_name, patient.first_name].filter(Boolean).join(', ').trim()
  return fullName || `Patient ${patient.patient_id}`
}

export const formatProviderName = (provider: Provider): string => {
  const fullName = [provider.first_name, provider.last_name].filter(Boolean).join(' ').trim()
  return fullName || `Provider ${provider.emr_id}`
}

export const mapProvidersToOptions = (providers: Provider[]): IOption[] => {
  return providers.map((provider) => ({
    code: provider.emr_id.toString(),
    name: formatProviderName(provider),
  }))
}

const parseNumericCode = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isNaN(value) ? null : value
  if (typeof value === 'string' && value.trim() !== '') {
    const parsedCode = Number(value)
    return Number.isNaN(parsedCode) ? null : parsedCode
  }
  return null
}

const resolvePatientStatusCode = (patient: Patient): number | null => {
  return parseNumericCode(patient.status?.code ?? patient.status_code ?? null)
}

const resolveAppointmentStartTime = (patient: Patient): string | null => {
  const appointmentIntervalStart = patient.appointment_time_interval?.split('-')[0]?.trim()
  return (
    appointmentIntervalStart ||
    patient.appointment_start_time ||
    patient.appointment_time ||
    patient.start_time ||
    null
  )
}

const formatAppointmentTimeUs = (appointmentTime: string | null): string | null => {
  if (!appointmentTime) return null

  for (const format of APPOINTMENT_TIME_INPUT_FORMATS) {
    const parsedTime = dayjs(appointmentTime, format, true)
    if (parsedTime.isValid()) {
      return parsedTime.format('h:mmA')
    }
  }

  const parsedTime = dayjs(appointmentTime)
  if (parsedTime.isValid()) {
    return parsedTime.format('h:mmA')
  }

  return null
}

const resolveConsultationStaffInitials = (patient: Patient): string => {
  const consultationStaffName = (patient.consultation_staff_name || '').trim()

  if (!consultationStaffName) {
    return ''
  }

  const nameParts = consultationStaffName.replace(',', ' ').split(/\s+/).filter(Boolean)
  const firstName = nameParts[0] || ''
  const lastName = nameParts[nameParts.length - 1] || ''
  const initials = UtilService.getInitials(firstName, lastName)

  if (initials) {
    return initials
  }

  if (firstName) {
    return firstName.slice(0, 2).toUpperCase()
  }

  return ''
}

const resolveProviderInitials = (fullName: string): string => {
  const nameParts = fullName.replace(',', ' ').split(/\s+/).filter(Boolean)
  const firstName = nameParts[0] || ''
  const lastName = nameParts[nameParts.length - 1] || ''
  const initials = UtilService.getInitials(firstName, lastName)

  if (initials) {
    return initials
  }

  if (firstName) {
    return firstName.slice(0, 2).toUpperCase()
  }

  return ''
}

const formatEncounterDateLabel = (encounter: PatientEncounter): string => {
  if (encounter.encounter_date_formatted) {
    return encounter.encounter_date_formatted
  }

  return new Intl.DateTimeFormat('en-US').format(new Date(encounter.encounter_date))
}

export const mapPatientsToOptions = (
  patients: PatientWithEncounters[],
  patientStatuses: PatientStatus[] = [],
): PatientSelectOption[] => {
  const patientStatusColorByCode = new Map(
    patientStatuses.map((status) => [status.code, status.color] as const),
  )

  return patients.map((patientWithEncounters) => {
    const patientName = formatPatientName(patientWithEncounters.patient)
    const staffInitials = resolveConsultationStaffInitials(patientWithEncounters.patient)
    const statusCode = resolvePatientStatusCode(patientWithEncounters.patient)
    const appointmentTimeLabel = formatAppointmentTimeUs(
      resolveAppointmentStartTime(patientWithEncounters.patient),
    )
    const statusColor =
      statusCode !== null ? patientStatusColorByCode.get(statusCode) || null : null
    const fullLabel = appointmentTimeLabel
      ? `${patientName} - ${appointmentTimeLabel}`
      : patientName

    return {
      code: patientWithEncounters.patient.patient_id.toString(),
      name: fullLabel,
      patientName,
      staffInitials,
      appointmentTimeLabel,
      statusCode,
      statusColor,
      searchText: fullLabel,
    }
  })
}

export const mapEncountersToOptions = (encounters: PatientEncounter[]): EncounterSelectOption[] => {
  return encounters.map((encounter) => {
    const dateLabel = formatEncounterDateLabel(encounter)
    const encounterTitle = encounter.encounter_title
    const providerName = encounter.provider?.full_name?.trim() || ''
    const providerInitials = resolveProviderInitials(providerName)
    const displayName = [dateLabel, encounterTitle].filter(Boolean).join(' ')
    const searchText = [providerName, providerInitials, displayName].filter(Boolean).join(' ')

    return {
      code: encounter.encounter_uuid,
      name: displayName,
      encounterTitle,
      dateLabel,
      providerInitials,
      providerName,
      searchText,
    }
  })
}

export const findPatientById = (
  patients: PatientWithEncounters[],
  patientId: number,
): PatientWithEncounters | undefined => {
  return patients.find((p) => p.patient.patient_id === patientId)
}

export const getFirstEncounterUuid = (
  patient: PatientWithEncounters | undefined,
): string | null => {
  if (!patient?.encounters || patient.encounters.length === 0) {
    return null
  }
  return patient.encounters[0].encounter_uuid
}

export const validateEncounterUuid = (
  patient: PatientWithEncounters | undefined,
  encounterUuid: string,
): boolean => {
  if (!patient?.encounters || patient.encounters.length === 0) {
    return false
  }
  return !!patient.encounters.find((e) => e.encounter_uuid === encounterUuid)
}

export const mapEncounterDataToPatientEncounter = (
  encounterData: EncounterData[],
): PatientEncounter[] => {
  return encounterData.map((enc) => ({
    encounter_uuid: enc.euuid,
    encounter_title: enc.reason || 'No Title',
    encounter_date: enc.date,
    encounter_date_formatted: enc.date_formatted,
    provider: enc.provider,
  }))
}

// Search-list rows omit contact details (email, referral source, discounts);
// prefer the full patient record, falling back to the row when the fetch fails.
export const resolveFullPatient = async (
  patient: IPatient,
  fetchPatientById: (patientId: number) => Promise<IPatient | null | undefined>,
): Promise<IPatient> => {
  if (!patient.id) return patient
  try {
    const fullPatient = await fetchPatientById(patient.id)
    return fullPatient ?? patient
  } catch {
    return patient
  }
}

const toDiscountOptionId = (value: PatientDiscountApiValue | undefined): string | null => {
  if (!value) return null
  return typeof value === 'string' ? value : value.option_id || null
}

export const createPatientWithEncounters = (
  patient: IPatient,
  encounters: PatientEncounter[],
): PatientWithEncounters => {
  const dob = normalizeDateString(patient.dob)

  return {
    patient: {
      patient_id: patient.id,
      first_name: patient.first_name,
      last_name: patient.last_name,
      consultation_staff: patient.consultation_staff ?? null,
      consultation_staff_name: patient.consultation_staff_name || '',
      age: dob ? dayjs().diff(dayjs(dob), 'year') : null,
      dob,
      phone: patient.phone || '',
      email: patient.email ?? null,
      sex: patient.sex,
      address: patient.address || '',
      city: patient.city || '',
      state: patient.state || '',
      zip_code: patient.zip_code || '',
      country: patient.country || '',
      billing_note: patient.billing_note || '',
      general_note: patient.general_note || '',
      referral_source: patient.referral_source || '',
      discount_category: toDiscountOptionId(patient.discount_category),
      discount_type_products: toDiscountOptionId(patient.discount_type_products),
      discount_type_procedures: toDiscountOptionId(patient.discount_type_procedures),
    },
    encounters,
  }
}

export const getLatestEncounter = (encounters: PatientEncounter[]): PatientEncounter | null => {
  if (encounters.length === 0) return null
  return encounters.sort(
    (a, b) => new Date(b.encounter_date).getTime() - new Date(a.encounter_date).getTime(),
  )[0]
}
